import { WizEvent, WizEventManager, WizRoot } from '@wizeoo/akm-component-lib';
import { BaselineEngine, DataEngine as BaseDataEngine, DataLoadObj, DataLooper, EM_ACTION, FeiKeyDef, FeiVO as EngineFeiVO, FIELD_KIND, FormulaCoreEngine, LoopResult, VisionVO, WidgetLine, WizConstants, WidgetCell, LoopVO, COMPUTE_MODE } from '@wizeoo/akm-compute-lib';
import { WDSSortField, WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import {
  Baseline,
  BaselineValue,
  DataPack,
  DataPackSet,
  FormulaEngineInstance,
  Measure,
  VisionStep,
  WidgetConfigurationObj
} from '@wizeoo/akm-message-lib';
import { cloneDeep } from 'lodash-es';
import { UpdateEntry, UpdateRequest } from '../../command/core/sync/sync.service';
import { AlkEvent } from '../../framework/events/alk.event';
import { ALK_EVENT_TYPE } from '../../model/enums';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { AggregateEngine } from '../computer/aggregate-engine';
import { DataMessage } from './data.message';

/**
 * Moteur de gestion des données (baselines)
 */
export class DataEngine extends BaseDataEngine {

  constructor(model: Model) {
    super(model);
  }
  public get model(): Model {
    return this._model as Model;
  }

  /* ##############################################################################
        détection d'un changement de locale pour recalcul ciblé
        ou mieux, alimentation des valeurs localisées
        L'écouteur est posé lors de la création du FEI
  ############################################################################### */

  /** 
   * pose unique d'un écouteur pour le changement de locale 
   * sur override newFeiVO(...
   */
  private localeListener: boolean = false;

  /**
   * changement de locale: recalcul éventuel des mesures du fei.translatableModeMatrix
   * @override
   * @param e: wizEvent 
   */
  public override localizeMe(e: WizEvent): void {
    // console.log('common locale changed', e);

    /**
     * construction de la map des pendings, pour loop
     * fei.level cible
     * scenario
     *  => Set de IBaselineValue
     */
    const loopvo: LoopVO = { loopResults: new Map(), pendings: new Map(), trace: false };
    let pendingMap: Map<number, Map<string, Set<WidgetCell>>> = loopvo.pendings;

    this.flatFeiCollection()?.forEach(fei => {
      if(fei.translatableModeMatrix?.length > 0) { // le FEI a des données traductibles

        // alimentation des pendings
        if(!pendingMap.has(fei.visionStepLevel)) {
          pendingMap.set(fei.visionStepLevel, new Map());
        }
        if(!pendingMap.get(fei.visionStepLevel).has(fei.scenario)) {
          pendingMap.get(fei.visionStepLevel).set(fei.scenario, new Set());
        }


        let blv: WidgetCell; // les widgetCells qu'on va rincer

        fei.getAllLines().forEach((wl: WidgetLine) => {
          wl.rendererMap = null; // on rince les rendus, ils sont obsolètes
          fei.translatableModeMatrix.forEach(mm => {
            // on rince les valeurs sur la IbaselineValue traductibles, pour recalcul
            blv = this.model.cellMatrixEngine.getCell(fei.visionStepLevel, wl.key, fei.scenario, mm.code);
            if(blv) {
              pendingMap.get(fei.visionStepLevel).get(fei.scenario).add(blv);
            }
          });
        });
      }
    });

    // console.log('[LMA TRS]reComputeWorkingObject', reComputeWorkingObject);

    // tour de looper si présence de pendings
    if (loopvo.pendings.size > 0) {
      
      this.model.cellMatrixEngine.forgetFDVLabels(); // on fait perdre la mémoire aux AkmFDVCells
      // console.log('recomputePendings', pendings.map);
      this.recomputePendings(loopvo); // et on joue les pendings
    }

    // dispatch de l'event de reconstruction pour refresh des widgets
    this.finalizeCompute(loopvo.loopResults, null);
  }

  /**
   * détermine le format de rendu selon le datakind d'un FKD
   * utilisé dans les formulaires
   * @param fkd: FeiKeyDef
   * @returns FIELD_KIND
   */
  public rendererKind(inKind: string): string {
    let result: string = FIELD_KIND.ANY; // any par défaut
    switch (inKind) {
      case FIELD_KIND.INT:
      case FIELD_KIND.NUMBER:
        result = FIELD_KIND.NUMBER;
        break;
      case FIELD_KIND.BOOLEAN:
        result = FIELD_KIND.BOOLEAN;
        break;
      case FIELD_KIND.DATE:
        result = FIELD_KIND.DATE;
        break;
      case FIELD_KIND.TIME:
        result = FIELD_KIND.TIME;
        break;
      case FIELD_KIND.STRING:
      case FIELD_KIND.CONSTANT:
        result = FIELD_KIND.STRING;
        break;
      case FIELD_KIND.COMMENT:
        result = FIELD_KIND.COMMENT;
        break;
      case FIELD_KIND.ACTION:
        result = FIELD_KIND.ACTION;
        break;
      case FIELD_KIND.BINARY:
      case FIELD_KIND.BINARY_MULTI:
        result = FIELD_KIND.BINARY;
        break;
      case FIELD_KIND.COLOR:
        result = FIELD_KIND.COLOR;
        break;
      case FIELD_KIND.FILE:
        result = FIELD_KIND.FILE;
        break;
      case FIELD_KIND.WORKFLOW:
        result = FIELD_KIND.WORKFLOW;
        break; 
      case FIELD_KIND.FILTER:
        result = FIELD_KIND.FILTER;
        break;  
      case FIELD_KIND.CROSS_FILTER:
        result = FIELD_KIND.CROSS_FILTER;
        break;           
      case FIELD_KIND.LIST:
        result = FIELD_KIND.LIST;
        break;
      case FIELD_KIND.THRESHOLD:
        result = FIELD_KIND.THRESHOLD;
        break;                              
      default:
        result = FIELD_KIND.ANY;
        break;
    }
    return result;
  }

  /**
   * en cas de suppression en cascade, retourne le niveau max des steps chargés
   * pour éviter un retour de commit sur des éléments non montés en mémoire
   * @param startLevel niveau de départ du delete
   * @returns niveau max
   */
  public getMaxDeep(startLevel: number): number {
    let result: number = startLevel;
    this.flatFeiCollection().forEach((fei: EngineFeiVO) => {
      result = Math.max(result, fei.visionStepLevel);
    });

    return result;
  }

  /* ##############################################################################
            Création des Feis et calcul des dépendances
    ############################################################################### */

  /**
     * Création d'un nouvel FEI
     * on pose l'écouteur de changement de locale
     */
  protected override newFeiVO(source: FormulaEngineInstance, v: VisionVO): FeiVO {
    
    if(!this.localeListener) { // écouteur unique de changement de locale
      WizEventManager.addListener(this, WizRoot.root(), WIZ_EVENT_TYPE.LOCALE_CHANGED, this.localizeMe);
      this.localeListener = true;
    }
    
    return new FeiVO(source, this.model, v, source.visionStep, source.scenario);

  }

  /**
    * création d'un nouveau FEI sur alimentation de la DS d'un widget
    * Va déclencher les contrôles de dépendances et les demandes de chargement
    * @param v 
    * @param vs 
    * @param scenario
    * @param configuration: WidgetConfigurationObj pour le mode de calcul (local / compute)
    * @param noRegister dans le cas d'un appel depuis un global filter. On a besoin d'un FEI, mais pas de ses lignes
    * @param forceComputeMode: COMPUTE_MODE
    * @returns le feiVO
    */
  public override generateFei(v: VisionVO, vs: VisionStep, scenario: string, configuration: WidgetConfigurationObj, noRegister: boolean = false, forceComputeMode: COMPUTE_MODE = null): FeiVO {
    const proto: FormulaEngineInstance = new FormulaEngineInstance();
    proto.vision = v.source;
    if (!vs) {
      console.log('no vs', this);
    }
    proto.visionStep = vs;
    proto.scenario = scenario;
    let fei: FeiVO = this.newFeiVO(proto, v);
    // mémorisation du mode compute
    let computeMode: COMPUTE_MODE = configuration?.cm ? configuration.cm as COMPUTE_MODE : COMPUTE_MODE.LOCAL;
    // if(forceComputeMode) {
    //   computeMode = forceComputeMode;
    // }
    fei.computeMode = computeMode;

    // console.log('generateFei', fei.uniqueName, fei.computeMode);

    // construction de la map de clés du FEI
    BaselineEngine.buildKeys(fei);

    if (scenario === WizConstants.SCEN_KEY_CODE) {
      fei.linesLoaded = true; // il en a pas à lui, le pauvre...
    }
    if (!noRegister) {
      this.model.dataEngine.feedFei(fei); // enregistrement dans la map de FEI
      if(fei.computeMode === COMPUTE_MODE.LOCAL) {
        this.feedDependencies(fei, configuration); // alimentation de la matrice de dépendances du fei (seulement si calcul local)
      }
      
      this.askForLoad(fei, configuration); // demande de chargement   
    } else { // on le marque comme calculé
      fei.computed = true;
    }

    return fei;
  }

  /**
   * collecte des données depuis le widgetDataService
   * @param fei
   * @param configuration pour le mode de calcul compute
   */
  private askForLoad(fei: FeiVO, configuration: WidgetConfigurationObj): void {
    if (fei.scenario !== ViewData.SCEN_KEY_CODE) { // demande de chargement, mais pas pour les multiScen, ils n'existent pas...
      // console.log('askForLoad', fei.uniqueName, fei.computeMode);
      
      if(fei.computeMode === COMPUTE_MODE.LOCAL) {
        this.model.widgetDataService.loadWidgetData(fei.vision.source, fei.visionStep, fei.scenario, false);
      } else {
        this.model.widgetDataService.loadComputeData(fei,configuration); // switch sur le moteur back
      }
    } else {
      // TODO gestion du multiscen en compute
      // Pour un multiscen, il est possible que tout soit déjà disponible
      // On le vérifie et on met à jour le multiscen le cas échéant
      this.checkMultiscen(fei);
    }
  }

  /**
 * ##### étape 1
 * appelé par le widgetDataService sur fin de chargement d'un dataload
 * contrôle que toutes les dépendances sont disponibles
 * @param dlo l'objet de chargement des données
 */
  public override checkAllDependenciesReady(dlo: DataLoadObj): void {
    // on délégue la vérification des dépendances au dataEngine du module akm-compute-lib
    super.checkAllDependenciesReady(dlo);

    // Si les lignes sont déja calculées, on finalise
    if (dlo.remoteComputed) {
      this.dloRemoteComputed(dlo);
    } else { // Si les lignes ne sont pas encore calculées
      // étape suivante, on tente de construire les WidgetLines
      this.checkAllWorkingPoolReady();
    }
  }

  /* ##############################################################################
          Calcul du pool de données
  ############################################################################### */

  /**
   * ##### étape 2
   * appelé parcheckAllDependenciesReady
   * construction des lignes pour les FEIs dont toutes les dépendances sont dispos
   */
  public checkAllWorkingPoolReady(): void {

    // le tour de boucle a déclenché une modification
    let hasUpdate: boolean = false;
    do { // on boucle en cas de modif... Si un enfant et OK, je le suis peut-être aussi...
      hasUpdate = false; // on considère PAS d'update
      this.flatFeiCollection().sort((a,b) => a.visionStepLevel - b.visionStepLevel).forEach((fei: EngineFeiVO) => { // on contrôle tout le monde

        if (!fei.workingPoolReady) { // inutile si déjà construit
          if (!!fei.allDependenciesReady) { // toutes les dépendances sont chargées
            // console.log('checkAllWorkingPoolReady', fei);
            if (this.feedWorkingPool(fei)) {
              hasUpdate = true; // on a construit les lignes
            }
          }
        }
      });

    } while (hasUpdate);
  }

  /* ##############################################################################
          Moteur de calcul Front
  ############################################################################### */

  private computePile: EngineFeiVO[] = [];

  protected override computeFei(fei: EngineFeiVO): void {
    this.computePile.push(fei);
    if (this.computePile[0] === fei) {
      this.doComputeFei(fei);
    }
  }

  /**
   * déclenchement du calcul initial
   * @param fei 
   */
  private doComputeFei(fei: EngineFeiVO): void {
    // Si le FEI n'est pas encore calculé et s'il ne s'agit pas d'un multiscen
    // Pour les Multiscens, ce sont les FEIs enfants qui calculent leurs lignes. Les multiscens n'ont pas besoin d'être calculés.
    // Ce ne sont que des aggrégats des FEIs enfants
    if (!fei.computed && fei.scenario !== ViewData.SCEN_KEY_CODE) {
      // 
      let dlo: DataLoadObj = this.model.widgetDataService.getDataloadByKey(fei.visionCode, fei.visionStepLevel, fei.scenario);
      dlo.startTime = new Date().getTime(); // début mesure
      this.model.dataMessage.doMessage(DataMessage.COMPUTE_INIT, dlo);
      setTimeout(() => {
        let lines: WidgetLine[] = [...fei.lines.get(fei.scenario).values()];
        FormulaCoreEngine.computeLines(fei, lines, false, this.reallyDoComputeFei.bind(this, dlo)); // calcul en mode non-silencieux      

        // dlo.endTime = new Date().getTime(); // fin mesure
        // dlo.totalComputeTime = (dlo.endTime - dlo.startTime) / 1000;
        // dlo.state = DataLoadObj.COMPLETE; // on a calculé au moins une fois
        // fei.computed = true;
        // WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.FEI_COMPUTED, fei));
        // this.checkMultiscens(this.flatFeiCollection()); // on a peut-être ce qu'il faut pour un multiScen 
        // this.model.dataMessage.doMessage(DataMessage.COMPUTE_ENDED, dlo);
      }, 0);
    }

    setTimeout(() => {
      // console.log('computeFei Ended', fei, this.computePile);
      this.computePile.reverse().pop();
      this.computePile = this.computePile.reverse();
      if (this.computePile.length > 0) {
        this.doComputeFei(this.computePile[0]);
      }
    }, 0);
  }

  /**
   * retour "asynchrone" par callback sur le calcul initial, pour laisser le temps async d'appel des matrices
   * @param dlo 
   * @param fei 
   */
  private reallyDoComputeFei(dlo: DataLoadObj, fei: FeiVO ): void {
    // console.log('reallyDoComputeFei', fei, dlo);
    dlo.endTime = new Date().getTime(); // fin mesure
    dlo.totalComputeTime = (dlo.endTime - dlo.startTime) / 1000;
    dlo.state = DataLoadObj.COMPLETE; // on a calculé au moins une fois
    fei.computed = true;
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.FEI_COMPUTED, fei));
    this.checkMultiscens(this.flatFeiCollection()); // on a peut-être ce qu'il faut pour un multiScen 
    this.model.dataMessage.doMessage(DataMessage.COMPUTE_ENDED, dlo);

  }

  public dloRemoteComputed(dlo: DataLoadObj) {
    let fei: FeiVO = this.getFei(COMPUTE_MODE.LOCAL, dlo.visionStep.level, dlo.scenario) as FeiVO;
    if (!fei.computed && fei.scenario !== ViewData.SCEN_KEY_CODE) {
      dlo.state = DataLoadObj.COMPLETE; // Tout est terminé
      fei.computed = true;
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.FEI_COMPUTED, fei));
      this.checkMultiscens(this.flatFeiCollection()); // on a peut-être ce qu'il faut pour un multiScen 
      this.model.dataMessage.doMessage(DataMessage.COMPUTE_REMOTE_ENDED, dlo);
    }
  }

  /**
   * Vérification et mise à jour d'un fei multiscen
   * Passage éventuel à computed sur les multiScen qui disposent de tous leurs FEI chargés
   * @param fei le fei multiscen
   * @returns un booléen indiquant si le fei multiscen a été mis à jour à computed
   */
  public override checkMultiscen(fei: EngineFeiVO): boolean {
    // On délégue la vérification et la mise à jour au dataengin du module akm-compute-lib 
    const multiscenComputed = super.checkMultiscen(fei);
    // Mais on déclenche un événement si le multiscen a été mise à jour à computed
    if (multiscenComputed) {
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.FEI_COMPUTED, fei));
    }
    return multiscenComputed;
  }

  /* ################################################
          gestion des updates avant commit
   ################################################ */

  /** 
   * fractionnement de la valeur pour ventilation sur un pool de lignes
   * @param updateRequest 
   * @returns un pack d'updates
   */
  public SplitValue(updateRequest: UpdateRequest): UpdateEntry[] {

    let updates: UpdateEntry[] = []; // la collection d'updates à retourner

    let fei: FeiVO = updateRequest.fei as FeiVO;
    let lines: WidgetLine[] = updateRequest.lines;
    let measure: Measure = updateRequest.fkd.measure;
    let value: number = updateRequest.value;

    if (value !== null && lines?.length > 0) {

      // ROUND sur nb decimal => ventilation d'un entier sur INT
      const precision: number = !!measure.nbDecimal ? measure.nbDecimal : 0;

      // la valeur qu'on voudrait
      const splittedNum: number = Math.floor(value * Math.pow(10, precision) / lines.length) / Math.pow(10, precision);
      //... mais la valeur qu'on a avec l'arrondi autorisé
      const splittedVal: string = splittedNum.toFixed(precision);

      // on calcule le delta avec l'arrondi
      let delta: number = Number((value - Number(splittedVal) * lines.length).toFixed(precision));
      // console.log('delta', value, lines.length, splittedVal, delta);
      let deltaNbParts: number = Math.floor(delta * Math.pow(10, precision)); // sur combien de parts on peut le ventiler (selon la précision)
      let deltaPart: number = delta; // une part du delta, par défaut, tout
      if (delta && deltaNbParts === 0) { // nombre de parts possibles
        deltaNbParts = 1;
      }
      if (deltaNbParts > 0) { // calcul du poids d'une part
        deltaPart = Number((delta / deltaNbParts).toFixed(precision));
      }

      // console.log('deltaNbParts', delta, deltaNbParts, deltaPart);

      let update: UpdateEntry;
      lines.forEach((item: WidgetLine) => {
        let val: number = Number(splittedVal);//  + delta;
        if (deltaNbParts > 0) { // tant qu'il reste des parts à ventiler, je ventile
          val += deltaPart;
          deltaNbParts--;
        }

        update = this.generateUpdate(fei, item, updateRequest.fkd, val.toFixed(precision));
        if (update.oldValue !== update.newValue) {
          updates.push(update); // l'update met à jour la baselineValue
        }
      });
    }
    return updates;
  }

  /**
   * compare les 2 valeurs, renvoi true si elle correspondent
   * gère undefined et null comme égaux
   * @param val1 
   * @param val2 
   */
  public equals(val1: string, val2: string): boolean {
    let realVal1: string;
    let realVal2: string;
    if (val1 === undefined || val1 === null) {
      realVal1 = '@@null@@';
    } else {
      realVal1 = val1;
    }
    if (val2 === undefined || val2 === null) {
      realVal2 = '@@null@@';
    } else {
      realVal2 = val2;
    }

    return realVal1 === realVal2;

  }

  /**
   * construction d'une nouvelle entrée de modification (depuis le grid / le form)
   * Si la baseline porte l'opération DELETE, on est sur une suppression
   * @param fei
   * @param widgetLine porteuse
   * @param fkd: FeiKeyDef, champ concerné
   * @param newValue nouvelle valeur
   * @param sourceTrigger pour post-synchro sur les calculs matriciels
   * @returns l'update
   */
  public generateUpdate(fei: FeiVO, widgetLine: WidgetLine, fkd: FeiKeyDef, newValue: string, sourceTrigger?: string): UpdateEntry {

    let scenario: string = widgetLine.scen; // cible. La baseline n'est peut-être qu'un héritage, il faudra la créer
    let baseline: Baseline = widgetLine.bl;

    let baselineActionKey: string = null;
    if (!!fkd) { // présence d'un FKD, donc modif de filtre ou de value
      baselineActionKey = fkd.fullCode;
      baseline.operation = null; // on nettoie les faux positifs sur la Baseline
    } else if (baseline.operation === EM_ACTION.DELETE) {
      baselineActionKey = EM_ACTION.DELETE;
    } else if (baseline.operation === EM_ACTION.CREATE) {
      baselineActionKey = EM_ACTION.CREATE;
    } else if (baseline.operation === EM_ACTION.UPDATE) {
      baselineActionKey = EM_ACTION.UPDATE;
    }

    // clé unique pour identifier cet update scen@baselineKey@field
    let ukey: string = [baseline.scen, baseline.key, baselineActionKey].join(WizConstants.FD_PARTS_SEPARATOR);

    // interception du forçage en majuscule
    let meas: Measure = fkd?.measure;
    if (meas?.upper && newValue?.length > 0) {
      newValue = newValue.toUpperCase();
    };

    // creation de la base de l'UpdateEntry
    let result: UpdateEntry = {
      fei,
      baseline,
      field: fkd?.fullCode,
      oldValue: null, // ancienne valeur
      newValue, // nouvelle valeur
      baselineOperation: null, // l'opération sur la baseline
      baselineValueOperation: null, // l'opération sur la value
      ukey,
      sourceTrigger
    };

    if (baseline.operation === EM_ACTION.DELETE) { // suppression de baseline
      // rien à faire, c'est tout bon.
      result.baselineOperation = EM_ACTION.DELETE;
    }
    else if (baseline.operation === EM_ACTION.CREATE) {
      result.baseline = baseline;
      result.baselineOperation = EM_ACTION.CREATE;
    } else if (baseline.operation === EM_ACTION.UPDATE) {
      result.baseline = baseline;
      result.baselineOperation = EM_ACTION.UPDATE;
    }
    else if (scenario !== baseline.scen) { // création de BL sur le scenar
      let newBaseline: Baseline = BaselineEngine.cloneBaselineForNewScenario(baseline, scenario);
      result.baseline = newBaseline;
      result.baselineOperation = EM_ACTION.CREATE;
      if (fkd.isFilterKey) { // on va juste créer la baseline
        BaselineEngine.baselineSetFilterKeyValue(newBaseline, fkd.fullCode, newValue);
        // c'est OK, déjà alimenté lors du clonage de baseline
      } else { // sinon, on va aussi lui ajouter sa BaselineValue
        // result.baselineValue = 
        BaselineEngine.baselineSetValue(newBaseline, fkd.fullCode, newValue);
        // result.baselineValueOperation = EM_ACTION.CREATE;
      }
    } else { // modification de filterKey ou de mesure

      // ou création initiale de la BL
      if(!!fkd) {
        result.oldValue = BaselineEngine.getRawKeyOrValue(fei, widgetLine, fkd.fullCode);// on alimente oldValue

        if (fkd.isFilterKey) { // update de filterKey
          result.baselineOperation = EM_ACTION.UPDATE;
          // ici on est en train de modifier la filterKey. Je ne peux pas mettre à jour l'instance de BL, sinon je ne 
          // pourrais pas identifier le delta en retour de service
          // donc on clone et on met à jour un clone de la baseline
          result.baseline = cloneDeep(result.baseline); // on perd l'instance
          // et on applique la modif
          BaselineEngine.baselineSetFilterKeyValue(result.baseline, fkd.fullCode, newValue);
          // console.log('upd', result.baseline.fkeyjson, baseline.fkeyjson);

          // c'est tout bon, update sur la filterKey
          // on mets PAS à jour tout de suite !! 
          //BaselineEngine.baselineSetFilterKeyValue(baseline, fkd.fullCode, newValue);
          // BaselineEngine.checkFDVCell(fei, baseline, pkd.fullCode, 'code');
        } else if (fkd.isMeasure) {
          let blv: BaselineValue = !!baseline.values ? baseline.values[fkd.fullCode] : null;
          if (!!blv) { // la baseline existe, simple update
            // on peut mettre à jour tout de suite
            result.baselineValue = BaselineEngine.baselineSetValue(baseline, fkd.fullCode, newValue);
            result.baselineValueOperation = EM_ACTION.UPDATE;
            this.model.cellMatrixEngine.addCell(baseline.level, baseline.key, baseline.scen, fkd.fullCode, newValue);
          } else { // création de baselineValue, on est pas sur le bon scenar
            result.baselineValue = BaselineEngine.baselineSetValue(baseline, fkd.fullCode, newValue);
            result.baselineValueOperation = EM_ACTION.CREATE;
            this.model.cellMatrixEngine.addCell(baseline.level, baseline.key, baseline.scen, fkd.fullCode, newValue);
          }
        }
      }

    }
    // console.log('updateEntry', result);
    return result;
  }

  /**
   * rollback sur une demande de modif échouée
   * @param UpdateEntry 
   * @param loopResults pour jouer les refresh (sur erreur de commit)
   */
  public rollBackUpdate(update: UpdateEntry, loopResults?: Map<FeiVO, LoopResult>): void {

    // pas de rollback sur les opération de baselines, elles n'ont pas été jouée
    // pareil pour les majs de filterKeys.
    // donc simple rollBack sur les BLVs de type mesure
    let baseline: Baseline = update.baseline;
    // baseline.operation = null;
    let doRollback: boolean = false;
    if (update.baselineValueOperation === EM_ACTION.UPDATE) {
      this.model.cellMatrixEngine.addCell(baseline.level, baseline.key, baseline.scen, update.field, update.oldValue);
      baseline.values[update.field].valor = update.oldValue;
      // baseline.values[update.field].operation = null;
      doRollback = true;
    } else if (update.baselineValueOperation === EM_ACTION.CREATE) {
      // Tout pareil ?
      this.model.cellMatrixEngine.addCell(baseline.level, baseline.key, baseline.scen, update.field, update.oldValue);
      baseline.values[update.field].valor = update.oldValue;
      doRollback = true;
    }

    if (!!loopResults && doRollback) { // on est sur un retour de commit
      let fei: FeiVO = this.getFei(COMPUTE_MODE.LOCAL, baseline.level, baseline.scen) as FeiVO; // prise du fei;
      let loopResult: LoopResult = DataLooper.getLooperResult(loopResults, fei); // le résultat dans lequel on va pousser les updates
      let wl: WidgetLine = fei.getWidgetLineForKey(baseline.scen, baseline.key);
      loopResult.updated.set(wl, new Set());
    }


    // console.log('rollBackUpdate', update);
  }

  /* ################################################
          recalcul sur retour de commit
    ################################################ */

  /**
     * fin de traitement du recompute, on marque les FEIs comme calculés, et on envoie l'event
     * appelé aussi en cas de rollback sur erreur de service
     * @param loopResults 
     */
  public override finalizeCompute(loopResults: Map<EngineFeiVO, LoopResult>, filterKeyUpdates: Map<Baseline, Set<string>>): void {
    super.finalizeCompute(loopResults, filterKeyUpdates);
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.FEI_RECOMPUTED, [loopResults, filterKeyUpdates]));
  }

  /* ################################################
          travail sur fake
    ################################################ */

  /**
   * créer un fake depuis un fei
   * @param fei, le vrai
   * @return un fake
   */
  public generateFakeFEI(fei: FeiVO): FeiVO {
    const fakeSource: FormulaEngineInstance = new FormulaEngineInstance();
    fakeSource.baselines = {};
    const fakeFei: FeiVO = new FeiVO(fakeSource, this.model, fei.vision, fei.visionStep, WizConstants.SCEN_FAKE_KEY_CODE); // on construit un faux FEI, pour porter le calcul
    fakeFei.uniqueName = [fakeFei.visionStepLevel, fakeFei.scenario].join(ViewData.CONTEXT_SEPARATOR);
    fakeFei.matrix = new Map(fei.matrix); // on lui affecte la matrice de calcul réelle
    fakeFei.orderedMatrix = [...fei.orderedMatrix]; // on lui affecte la matrice de calcul réelle
    fakeFei.visionKeys = fei.visionKeys;
    fakeFei.filterKeys = fei.filterKeys;
    fakeFei.keys = fei.keys;
    fakeFei.keyMap = fei.keyMap;
    this.model.dataEngine.feedFei(fakeFei);
    return fakeFei;
  }


  /* ################################################
            suppression de la pile des FEI sur :
            modification de mesures
            modification de FD
            modification de chrono
     ################################################ */

  /**
   * sur changement de vision, supprimer la mémorisation des FEI
   * dans le dataEngine
   * dans les cellMatrix
   * dans le dataService
   */
  public resetDatas(): void {
    this.model.dataEngine.clearFei();
    this.model.cellMatrixEngine.clear();
    this.model.widgetDataService.resetAll();

  }

  /**
   * retourne le sortField par défaut pour un pkd
   * @param pkd cible
   * @returns le sort
   */
  public pkdSortField(fkd: FeiKeyDef): WDSSortField {
    return { property: 'sortKey', numeric: this.model.dataEngine.datakindIsNumeric(fkd?.datakind), way: 'asc' };
  }

  /**
   * retourne la fonction d'aggrégat par défaut pour ce type de measure
   * @param pkd cible
   * @returns code de la fonction d'aggrégat
   */
  public pkdDefaultAggregateFunction(datakind: string): string {
    let aggMethod: string = '';

    switch (datakind) {
      case FIELD_KIND.INT:
      case FIELD_KIND.NUMBER:
      case FIELD_KIND.CONSTANT:
        aggMethod = AggregateEngine.AGG_SUM;
        break;
      case FIELD_KIND.DATE:
        aggMethod = AggregateEngine.AGG_MIN;
        break;
      case FIELD_KIND.TIME:
        aggMethod = AggregateEngine.AGG_AVG;
        break;
      case FIELD_KIND.BOOLEAN:
        aggMethod = AggregateEngine.AGG_BOOLEAN;
        break;
      case FIELD_KIND.STRING:
      case FIELD_KIND.COMMENT:
        aggMethod = AggregateEngine.AGG_COUNT;
        break;
      case FIELD_KIND.LIST: // rendu en mode liste
      case FIELD_KIND.SUB_LINES: // rendu en mode liste du next step
      case FIELD_KIND.COLOR:
        aggMethod = AggregateEngine.AGG_LIST;
        break;
      default:
        aggMethod = AggregateEngine.AGG_COUNT;
        break;
    }
    return aggMethod;
  }

  /**
   * donne le dernier numRev de la map de commit
   * @returns 
   */
  public commitMapMaxRevision(): number {
    let res: number = -1;
    if(this.model.commitMap?.size > 0) {
      res = Math.max(...this.model.commitMap.keys());
    }
    return res;
  }

  /**
   * donne le dernier numRev de la map de feis locaux
   * @returns 
   */
  public localMaxRevision(fei: FeiVO): number {
    let res: number = -1;
    if(fei) {
      let dlo: DataLoadObj = this.model.widgetDataService.getDataloadByKey(fei.visionCode, fei.visionStepLevel, fei.scenario);
      if(!!dlo) {
        res = dlo.revisionNumber;
      }
    }
    return res;
  }

  /**
   * check si ce FEI (remote) est à jour en termes de révisions
   * @param fei 
   */
  public deltaRev(fei: FeiVO): boolean {
 
    let thisNumRev: number = fei?.computeRevNumber || -1;
    let commitNumRev: number = this.commitMapMaxRevision();
    if(commitNumRev < 0) { // pas de commit, on cherche dans la pile locale
      commitNumRev = this.localMaxRevision(fei);
    }

    if(commitNumRev > thisNumRev) {
      return true;
    }

    return false;
  }

  /**
   * donne le numRev Max d'un datapack sur retour de commit pour enregistrement dans la map des commits
   * @param pack 
   */
  public datapackMaxRevisionNumber(dataPack: DataPack): number {
      let commitRevNumber: number = -1;
      try {
        Object.values(dataPack.datas).forEach((p: {[key: string]: DataPackSet}) => {
            Object.values(p).forEach((ps: DataPackSet) => {
                if(ps.revNumber > commitRevNumber) {
                    commitRevNumber = ps.revNumber;
                }
            });
        });
      } catch(e: any) {

      }
      return commitRevNumber;
  }

  /**
   * donne le numRev Max d'un datapack sur retour de commit pour enregistrement dans la map des commits
   * @param pack 
   */
  public fastSyncRemoteGrid(dataPack: DataPack): void {
      try {
        Object.values(dataPack.datas).forEach((p: {[key: string]: DataPackSet}) => {
            Object.values(p).forEach((ps: DataPackSet) => {

              let remoteFEI: EngineFeiVO = this.feiMap.get(COMPUTE_MODE.COMPUTE)?.get(ps.level)?.get(ps.scenario);
              let existBL: Baseline; // la BL dispo dans le remote (ou pas)
              let existBLV: BaselineValue; // la BLV dispo dans le remote (ou pas)
              
              if(!!remoteFEI) {
                ps.baselineValues?.forEach(blv => {
                  existBL = remoteFEI.lines?.get(blv.scen)?.get(blv.key)?.bl;
                  if(existBL) {
                    existBLV = existBL.values ? existBL.values[blv.mkey] : null;
                    if(existBLV) { // la BLV existe, on met à jour sa valeur, sa version et son numRev
                      // console.log('got remote existBLV', existBLV.valor , blv.valor);
                      existBLV.valor = blv.valor;
                      existBLV.revisionNumber = blv.revisionNumber;
                      existBLV.version = blv.version; 
                    }
                  }
                });
                // refresh visuel
                WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.COMPUTE_FAST_SYNC, this));
                // console.log('got remote', remoteFEI);
              }
            });
        });
      } catch(e: any) {

      }
  }

  /**
   * collecte des dépendances de visions
   * limiter au nécessaire (pour ce FEI)
   * @param fei 
   * @override
   */
  public feedCrossVision(fei: FeiVO): void {
    
    // ajout de deps sur mesure de type crossFilter
    [...fei.keyMap.values()].forEach(fkd => {
      if(fkd.isMeasure && fkd.datakind === FIELD_KIND.CROSS_FILTER) {
        // prise des settings
        // console.log("loading crossed", fkd);
        let sets: any = fkd.measure.settings;
        if(!!sets) {
          let vision: string = sets.vision;
          let level: number = sets.level;
          if(vision && level) {
            fei.requiredCrossed.add([vision, level].join(WizConstants.CONTEXT_SEPARATOR));
          }
        }
      }
    });

    if(fei.requiredCrossed?.size > 0) {
      [...fei.requiredCrossed.keys()].forEach(crossKey => {
        if(!this.crossVisions.has(crossKey)) {
          // console.log("loading crossed", crossKey);
          this.crossVisions.set(crossKey, DataLoadObj.PENDING);

          let parts: string[]= crossKey.split(WizConstants.CONTEXT_SEPARATOR);
          // prise de la vision
          let vis: VisionVO = this.model.visionEngine.getVision(parts[0]);
          let vistep: VisionStep = vis.visionSteps[parts[1]];
          // console.log("loading vision", vis, vistep);
          // et on lance le chargement
          this.model.widgetDataService.loadWidgetData(vis.source, vistep, fei.scenario, true);

        }
      });
    }
  }


}
